import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

const V = "LERNO Co.";
const VC = "LER599869";
const ADDR = "Belleville, NJ 07109";
const C = {
  o: "orders@mosaicassessmentco.com",
  c: "compliance@mosaicassessmentco.com",
  s: "support@mosaicassessmentco.com",
  p: "privacy@mosaicassessmentco.com",
};
const mail = (e) => <a href={`mailto:${e}`} className="text-blue-700 hover:underline">{e}</a>;
const H1 = ({ id, children }) => <h1 id={id} className="text-3xl font-bold mt-12 mb-4 text-slate-900 scroll-mt-24">{children}</h1>;
const H2 = ({ children }) => <h2 className="text-xl font-semibold mt-8 mb-3 text-slate-800">{children}</h2>;
const P = ({ children }) => <p className="text-slate-700 leading-relaxed mb-3">{children}</p>;
const UL = ({ children }) => <ul className="list-disc pl-6 space-y-1 text-slate-700 mb-4">{children}</ul>;

export default function Legal() {
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) window.scrollTo(0, 0);
    else { const el = document.querySelector(hash); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); }
  }, [hash]);
  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-3xl mx-auto px-6 pt-10 pb-24" data-testid="legal-page">
        <Link to="/" className="text-sm text-blue-700 hover:underline">← Back to MOSAIC</Link>
        <div className="mt-4 mb-8 pb-6 border-b border-slate-200">
          <div className="text-xs uppercase tracking-wide text-teal-600 font-semibold">Legal & Compliance</div>
          <h1 className="text-4xl font-bold mt-2 text-slate-900">MOSAIC by {V}</h1>
          <p className="text-sm text-slate-500 mt-2">Last updated: {new Date().toISOString().slice(0,10)} · NYC Vendor Code {VC}</p>
          <nav className="mt-4 flex flex-wrap gap-4 text-sm">
            <a href="#privacy" className="text-blue-700 hover:underline">Privacy Policy</a>
            <a href="#terms" className="text-blue-700 hover:underline">Terms of Service</a>
            <a href="#sla" className="text-blue-700 hover:underline">Service Commitments</a>
            <a href="#compatibility" className="text-blue-700 hover:underline">Device Compatibility</a>
            <a href="#accessibility" className="text-blue-700 hover:underline">Accessibility</a>
            <a href="#compliance" className="text-blue-700 hover:underline">NYC Compliance</a>
            <a href="#trademark" className="text-blue-700 hover:underline">Trademark</a>
            <a href="#contact" className="text-blue-700 hover:underline">Contact</a>
          </nav>
        </div>

        <H1 id="privacy">Privacy Policy</H1>
        <P>{V} builds MOSAIC, an English language proficiency assessment and practice-materials product. This policy explains what information we collect, how we use it, and the rights of students, parents, and educators.</P>
        <H2>Data we do NOT collect</H2>
        <P>For our current print-only product (LERNO Print CO), we do <b>not</b> collect, receive, view, or store any student personally identifiable information (PII). No student rosters, scores, responses, audio, or images of students flow through {V} systems.</P>
        <H2>Data we do collect (from purchasers only)</H2>
        <UL>
          <li>School Purchase Order number and school name</li>
          <li>ELL Coordinator name + email address for delivery</li>
          <li>Optional additional recipient emails (for cc)</li>
          <li>Optional shipping address if a physical delivery is requested</li>
          <li>Timestamp and IP of downloads for security auditing</li>
        </UL>
        <H2>How we use it</H2>
        <UL>
          <li>Deliver the ordered print kit via a one-time watermarked download link</li>
          <li>Send license-expiry reminders (~30 days before June 30)</li>
          <li>Send optional renewal-window reminders (~15 days before the March 1 NYC DOE PO cutoff)</li>
          <li>Respond to support requests</li>
        </UL>
        <H2>How we do NOT use it</H2>
        <UL>
          <li>Never sold, shared, or transferred for marketing purposes</li>
          <li>No student ever interacts with {V} systems in the print product</li>
          <li>No automated processing is used to profile, grade, or make decisions about any student</li>
        </UL>
        <H2>Compliance frameworks</H2>
        <P>MOSAIC is designed to comply with FERPA, COPPA, NYS Ed Law §2-d, NYCPS Chancellor's Regulation A-820, and the NYCPS SY 2026-27 Automated Processing & Screen Time Policy. Detailed evidence artifacts are linked below.</P>
        <H2>Your rights</H2>
        <UL>
          <li><b>Access & deletion:</b> email {mail(C.p)} to request records or deletion.</li>
          <li><b>Response time:</b> 10 business days.</li>
          <li><b>Data retention:</b> order records retained 7 years post-fiscal-year for audit; then deleted.</li>
        </UL>

        <H1 id="terms">Terms of Service</H1>
        <P>By ordering MOSAIC print materials from {V}, the purchasing school or district agrees to the following terms.</P>
        <H2>License grant</H2>
        <P>Each MOSAIC print order licenses the named school to print and photocopy purchased Blackline Master (BLM) booklets, teacher editions, and included Additional Resources <b>for use at that single named school only, through the license expiration date printed on each page</b>. Licenses expire annually on June 30.</P>
        <H2>Permitted use</H2>
        <UL>
          <li>Unlimited internal printing and photocopying at the named school</li>
          <li>Distribution to teachers, proctors, and enrolled students at the named school</li>
          <li>Storage of the delivered PDFs on the school's internal file server for staff access</li>
        </UL>
        <H2>Prohibited use</H2>
        <UL>
          <li>Redistribution of digital PDFs to other schools, districts, or third parties</li>
          <li>Posting the delivered files to any public or non-authenticated web location</li>
          <li>Removing, altering, or obscuring the watermark, license footer, or copyright notice</li>
          <li>Reselling, sublicensing, or offering the materials on any marketplace</li>
        </UL>
        <H2>Delivery</H2>
        <P>Delivered as a watermarked ZIP via a single-use magic link to the school's ELL coordinator. Links expire six hours after issuance; self-serve resend at <Link to="/download-lookup" className="text-blue-700 hover:underline">/download-lookup</Link>. Delivery target: within one business day of PO receipt.</P>
        <H2>Refunds & replacements</H2>
        <P>Once the ZIP has been downloaded, the order is fulfilled and non-refundable. Damaged or corrupted downloads will be re-issued at no charge — contact {mail(C.s)} within 30 days.</P>
        <H2>Disclaimer of endorsement</H2>
        <P>MOSAIC is a WIDA-format practice and preparation product. MOSAIC is <b>not the WIDA ACCESS assessment</b>, is <b>not the NYSESLAT</b>, and is <b>not affiliated with, endorsed by, or a substitute for</b> WIDA, the Wisconsin Center for Education Research, the New York State Education Department, or NYCPS. Schools must administer WIDA ACCESS through WIDA AMS for official English Language Proficiency determination.</P>
        <H2>Governing law</H2>
        <P>These terms are governed by the laws of the State of New York. Any dispute will be resolved in New York County, NY.</P>

        <H1 id="sla">Service Commitments</H1>
        <P>{V} publishes these commitments so procurement and school leadership know exactly what to expect. They apply to every LERNO print order.</P>
        <H2>Delivery</H2>
        <UL>
          <li><b>Within 1 business day</b> of PO receipt (Mon-Fri, 9AM-5PM Eastern)</li>
          <li>Emergency same-day delivery available on request during the Feb-April WIDA ACCESS test window</li>
          <li>Delivery method: SendGrid magic link to the school's ELL coordinator + optional CC recipients</li>
        </UL>
        <H2>Support</H2>
        <UL>
          <li><b>Response time:</b> within 24 hours on business days at {mail(C.s)}</li>
          <li><b>Coverage window:</b> 9AM-5PM Eastern, Mon-Fri (extended to 8AM-8PM Eastern during Feb-April test window)</li>
          <li><b>Escalation:</b> unresolved issues after 48 hours → {mail(C.c)}</li>
        </UL>
        <H2>Portal availability</H2>
        <UL>
          <li><b>99.9% uptime target</b> during Feb-April WIDA ACCESS test window</li>
          <li>Self-serve magic-link resend available 24/7 at <Link to="/download-lookup" className="text-blue-700 hover:underline">/download-lookup</Link></li>
          <li>Corrupted or damaged downloads re-issued at no charge within 30 days</li>
          <li>Infrastructure: MongoDB Atlas (SOC 2 Type II), Cloudflare R2 (SOC 2 Type II), SendGrid (SOC 2 Type II) — all with 99.9%+ SLAs</li>
        </UL>

        <H1 id="compatibility">Device & File Compatibility</H1>
        <P>NYCPS's default student device is Chromebook. All MOSAIC materials are validated against ChromeOS as a first-class target.</P>
        <H2>Verified compatible</H2>
        <UL>
          <li><b>ChromeOS / Chromebook</b> — Files app + built-in PDF viewer render every BLM and every Additional Resource without pixelation</li>
          <li><b>Windows 10/11</b> — Edge, Chrome, Firefox, Adobe Reader</li>
          <li><b>macOS</b> — Preview, Chrome, Firefox</li>
          <li><b>iPad / iOS</b> — Files app, Chrome, Safari</li>
        </UL>
        <H2>File format</H2>
        <UL>
          <li>All PDFs are standard PDF 1.7, machine-readable text (not scanned images) — screen readers work</li>
          <li>All ZIPs use standard DEFLATE compression — no additional software required</li>
          <li>Watermarks tested legible on both color and grayscale printing</li>
          <li>Print flow verified: ChromeOS → Google Cloud Print → any HP/Xerox/Ricoh school printer</li>
        </UL>

        <H1 id="accessibility">Accessibility Statement</H1>
        <P>{V} is committed to ensuring MOSAIC materials are usable by educators, students, and families of all abilities.</P>
        <H2>Print materials</H2>
        <UL>
          <li>PDF text is machine-readable (not scanned images) — screen readers can read every question</li>
          <li>Grades K-2 items printed in color; grades 3-12 remain readable in grayscale (no color-only cues)</li>
          <li>Body text is minimum 12pt K-5 and 11pt 6-12; heading hierarchy preserved</li>
          <li>Standard accommodations (extended time, small group, read-aloud for non-reading modalities, home-language directions) documented in the Teacher Administration Guide</li>
        </UL>
        <H2>Website</H2>
        <P>This website targets WCAG 2.1 Level AA. If you encounter an accessibility barrier, please email {mail(C.s)} — we'll respond within 5 business days.</P>

        <H1 id="compliance">NYC Compliance & Evidence Documents</H1>
        <P>{V} publishes a full evidence packet for district IT, privacy, and procurement officers.</P>
        <H2>Vendor details</H2>
        <UL>
          <li><b>Legal entity:</b> {V}</li>
          <li><b>NYC Vendor Code:</b> {VC}</li>
          <li><b>Product line:</b> MOSAIC — WIDA-aligned print instructional materials</li>
          <li><b>DUNS / SAM.gov:</b> available on request</li>
        </UL>
        <H2>Compliance framework alignment</H2>
        <UL>
          <li>NYCPS SY 2026-27 Automated Processing & Screen Time Policy — <b>Print product uses no student-facing automated processing</b></li>
          <li>NYCPS ERMA — <b>Print product is out of scope</b> (no student PII, no APPR data)</li>
          <li>NYS Ed Law §2-d — Signed DPA template available</li>
          <li>NYCPS Chancellor's Regulation A-820 — Aligned</li>
          <li>FERPA / COPPA / PPRA — No student data processed</li>
        </UL>
        <H2>Downloadable evidence artifacts</H2>
        <UL>
          <li><a href="/docs/MOSAIC_AI_Transparency_OnePager.pdf" className="text-blue-700 hover:underline" data-testid="legal-link-ai">AI Transparency One-Pager (SY 2026-27 policy alignment)</a></li>
          <li><a href="/docs/MOSAIC_NYS_Section_2d_DPA.pdf" className="text-blue-700 hover:underline" data-testid="legal-link-dpa">NYS Ed Law §2-d Data Privacy Agreement (signature-ready)</a></li>
          <li><a href="/blm_downloads/MOSAIC_ERMA_Compliance_White_Paper.pdf" className="text-blue-700 hover:underline" data-testid="legal-link-erma">ERMA Compliance White Paper (19 sections)</a></li>
          <li><a href="/sales/MOSAIC_TeacherEdition_SoleVendor_Letter.pdf" className="text-blue-700 hover:underline" data-testid="legal-link-solesrc">NYC Sole-Vendor Letter — MOSAIC Teacher Edition (K–12)</a></li>
        </UL>

        <H1 id="trademark">Trademark & Endorsement Disclaimer</H1>
        <P><b>MOSAIC</b> is a WIDA-format practice and preparation product produced by {V} MOSAIC is <b>NOT the WIDA ACCESS assessment</b>, is <b>NOT the NYSESLAT</b>, and is <b>NOT affiliated with, endorsed by, or a substitute for</b> WIDA, the Wisconsin Center for Education Research (WCER), the New York State Education Department (NYSED), or the New York City Public Schools (NYCPS). Schools must administer WIDA ACCESS through WIDA AMS for official English Language Proficiency determination.</P>
        <H2>Nominative fair use</H2>
        <P>"WIDA" and "ACCESS" are trademarks of the Board of Regents of the University of Wisconsin System. Their use on this website and in MOSAIC materials is <b>nominative fair use</b> — used solely to accurately describe the WIDA-aligned format of our practice materials and their compatibility with WIDA ACCESS test preparation. No sponsorship, endorsement, or affiliation is implied.</P>
        <H2>Other referenced marks</H2>
        <P>NYSESLAT is a program of the New York State Education Department. NYCPS, NYC Public Schools, and NYC DOE are marks of the New York City Department of Education. ChromeOS and Chromebook are trademarks of Google LLC. All other product names, logos, and brands referenced on this site are property of their respective owners.</P>

        <H1 id="contact">Contact</H1>
        <UL>
          <li><b>Orders / New POs:</b> {mail(C.o)}</li>
          <li><b>District IT / Privacy Officers:</b> {mail(C.c)}</li>
          <li><b>Support (existing customers):</b> {mail(C.s)}</li>
          <li><b>Privacy requests:</b> {mail(C.p)}</li>
          <li><b>Mailing address:</b> {V}, {ADDR}</li>
        </UL>
      </div>
    </div>
  );
}
